import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Check, 
  X, 
  Crown, 
  Sparkles, 
  TrendingUp, 
  Brain,
  Star,
  Shield,
  Zap,
  Award,
  Calendar,
  Apple,
  Calculator,
  MessageSquare,
  Target,
  Loader2,
  CheckCircle,
  RefreshCw,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export const PremiumPage = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  // Check premium status on mount
  useEffect(() => {
    const checkPremiumStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setCheckingStatus(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/ai/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPremiumStatus(response.data);
      } catch (error) {
        console.error('Error checking premium status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkPremiumStatus();
  }, []);

  // Calculate premium expiry info
  const getPremiumInfo = () => {
    if (!premiumStatus?.is_premium) return null;
    
    const expiryDate = premiumStatus.premium_until ? new Date(premiumStatus.premium_until) : null;
    const now = new Date();
    
    if (!expiryDate) return { active: true, expired: false };
    
    const isExpired = expiryDate < now;
    const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    
    return {
      active: !isExpired,
      expired: isExpired,
      expiryDate: expiryDate.toLocaleDateString('tr-TR'),
      daysLeft: isExpired ? 0 : daysLeft
    };
  };

  const premiumInfo = getPremiumInfo();

  const handleSubscribe = async (planId, planName, price) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Premium satın almak için lütfen giriş yapın');
      navigate('/login');
      return;
    }

    if (planId === 'free') {
      toast.info('Zaten ücretsiz plan kullanıyorsunuz');
      return;
    }

    setLoading(true);
    toast.info('Ödeme sayfası hazırlanıyor...');

    try {
      const response = await axios.post(
        `${API}/premium/initialize`,
        { plan_id: planId, plan_name: planName, price: price },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_page_url) {
        window.location.href = response.data.payment_page_url;
      } else if (response.data.checkout_form_content) {
        const checkoutWindow = window.open('', '_blank');
        checkoutWindow.document.write(response.data.checkout_form_content);
        checkoutWindow.document.close();
        toast.success('Ödeme sayfası açıldı!');
      } else {
        toast.error('Ödeme sayfası oluşturulamadı');
      }
    } catch (error) {
      console.error('Premium payment error:', error);
      toast.error(error.response?.data?.detail || 'Ödeme başlatılamadı');
    } finally {
      setLoading(false);
    }
  };

  // Render premium status banner
  const renderPremiumStatusBanner = () => {
    if (checkingStatus) {
      return (
        <div className="bg-purple-100 border border-purple-300 rounded-lg p-4 mb-8 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin mr-2 text-purple-600" />
          <span className="text-purple-700">Premium durumu kontrol ediliyor...</span>
        </div>
      );
    }

    if (!premiumStatus) return null;

    // Premium aktif
    if (premiumInfo?.active) {
      return (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-300 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <CheckCircle className="h-7 w-7 text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-green-800 flex items-center">
                  <Crown className="h-5 w-5 mr-2 text-yellow-500" />
                  Premium Aktif ✅
                </h3>
                <p className="text-green-600">
                  Bitiş tarihi: <span className="font-semibold">{premiumInfo.expiryDate}</span>
                  {premiumInfo.daysLeft > 0 && (
                    <span className="ml-2">({premiumInfo.daysLeft} gün kaldı)</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="border-green-500 text-green-700 hover:bg-green-50"
                onClick={() => navigate('/ai-dietitian')}
              >
                <Apple className="h-4 w-4 mr-2" />
                AI Diyetisyen
              </Button>
              <Button 
                variant="outline"
                className="border-gray-300"
                onClick={() => toast.info('Üyelik yönetimi yakında aktif olacak')}
              >
                <Settings className="h-4 w-4 mr-2" />
                Üyeliği Yönet
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Premium süresi dolmuş
    if (premiumInfo?.expired) {
      return (
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-300 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <RefreshCw className="h-7 w-7 text-orange-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-orange-800">
                  Premium Süresi Doldu
                </h3>
                <p className="text-orange-600">
                  Dolma tarihi: <span className="font-semibold">{premiumInfo.expiryDate}</span>
                </p>
              </div>
            </div>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => handleSubscribe('monthly', 'Aylık Premium', 149)}
              disabled={loading}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Premium'u Yenile
            </Button>
          </div>
        </div>
      );
    }

    return null;
  };

  // Get button text based on premium status
  const getButtonConfig = (plan) => {
    if (plan.id === 'free') {
      return { text: 'Mevcut Plan', disabled: true, variant: 'outline' };
    }

    if (premiumInfo?.active) {
      return { text: 'Premium Aktif ✅', disabled: true, variant: 'outline' };
    }

    if (premiumInfo?.expired) {
      return { text: 'Premium\'u Yenile', disabled: false, variant: 'default' };
    }

    return { text: plan.buttonText, disabled: false, variant: plan.buttonVariant };
  };

  const plans = [
    {
      id: 'free',
      name: 'Ücretsiz Üyelik',
      price: 0,
      priceDisplay: '₺0',
      period: '',
      features: [
        { text: 'Belirli ücretsiz dersler', included: true },
        { text: 'Temel AI Diyetisyen (kısıtlı)', included: true },
        { text: 'Haftada 1 beslenme planı', included: true },
        { text: 'Günde 1 kalori hesaplama', included: true },
        { text: 'Sınırsız kayıtlı ders', included: false },
        { text: 'AI Diyetisyen TAM SÜRÜM', included: false },
        { text: 'Hedef takibi', included: false },
      ],
      buttonText: 'Mevcut Plan',
      buttonVariant: 'outline',
      popular: false
    },
    {
      id: 'monthly',
      name: 'Aylık Premium',
      price: 149,
      priceDisplay: '₺149',
      period: '/ay',
      features: [
        { text: 'Sınırsız kayıtlı ders', included: true },
        { text: 'Premium AI Program', included: true },
        { text: 'Eğitmen özel içerikleri', included: true },
        { text: 'Derslere erken erişim', included: true },
        { text: 'AI Diyetisyen TAM SÜRÜM', included: true, highlight: true },
        { text: 'Sınırsız beslenme planı', included: true, highlight: true },
        { text: 'Sınırsız kalori-makro analizi', included: true },
        { text: 'Kişisel hedef takibi', included: true },
        { text: 'Tarif önerileri', included: true },
        { text: 'Haftalık gelişim analizi', included: true },
      ],
      buttonText: 'Premium Başlat',
      buttonVariant: 'default',
      popular: false
    },
    {
      id: 'yearly',
      name: 'Yıllık Premium',
      price: 1290,
      priceDisplay: '₺1.290',
      period: '/yıl',
      priceNote: 'Aylık ₺107,50 - %28 tasarruf',
      badge: 'En Popüler',
      features: [
        { text: 'Sınırsız kayıtlı ders', included: true },
        { text: 'Premium AI Program', included: true },
        { text: 'Eğitmen özel içerikleri', included: true },
        { text: 'Derslere erken erişim', included: true },
        { text: 'AI Diyetisyen TAM SÜRÜM', included: true, highlight: true },
        { text: 'Sınırsız beslenme planı', included: true, highlight: true },
        { text: 'Sınırsız kalori-makro analizi', included: true },
        { text: 'Kişisel hedef takibi', included: true },
        { text: 'Tarif önerileri', included: true },
        { text: 'Haftalık gelişim analizi', included: true },
        { text: '✨ Yıllık indirim avantajı', included: true, highlight: true },
      ],
      buttonText: 'Yıllık Planı Seç',
      buttonVariant: 'default',
      popular: true
    }
  ];

  const aiDietitianFeatures = [
    { icon: Calculator, title: 'Kalori ve Makro Hesaplama', description: 'Günlük kalori, protein, karbonhidrat ve yağ ihtiyacınızı hesaplayın' },
    { icon: Calendar, title: 'Haftalık Beslenme Planı', description: 'Türk mutfağına uygun, kişiselleştirilmiş 7 günlük beslenme programı' },
    { icon: MessageSquare, title: 'Diyetisyene Sor', description: 'AI diyetisyen ile sınırsız sohbet, beslenme tavsiyeleri alın' },
    { icon: Target, title: 'Hedef Takibi', description: 'Kilo, yağ oranı ve kas gelişiminizi takip edin' },
    { icon: Apple, title: 'Tarif Önerileri', description: 'Hedeflerinize uygun, yerel ve uygun fiyatlı tarif önerileri' },
    { icon: TrendingUp, title: 'İlerleme Analizi', description: 'Haftalık gelişim raporları ve kişiselleştirilmiş öneriler' }
  ];

  const comparisonFeatures = [
    { name: 'Sınırsız kayıtlı ders', free: false, premium: true },
    { name: 'Premium AI Program', free: false, premium: true },
    { name: 'Eğitmen özel içerikleri', free: false, premium: true },
    { name: 'Derslere erken erişim', free: false, premium: true },
    { name: 'AI Diyetisyen', free: 'Kısıtlı', premium: 'Tam Sürüm' },
    { name: 'Haftalık beslenme planı', free: '1/hafta', premium: 'Sınırsız' },
    { name: 'Kalori hesaplama', free: '1/gün', premium: 'Sınırsız' },
    { name: 'AI Sohbet', free: '3/gün', premium: 'Sınırsız' },
    { name: 'Hedef takibi', free: false, premium: true },
    { name: 'Alışveriş listesi', free: false, premium: true },
  ];

  const faqs = [
    { q: 'AI Diyetisyen ne işe yarar?', a: 'AI Diyetisyen, yapay zeka destekli kişisel beslenme asistanınızdır. Günlük kalori ve makro ihtiyacınızı hesaplar, Türk mutfağına uygun haftalık beslenme planları oluşturur, beslenme sorularınızı yanıtlar ve hedeflerinize ulaşmanızı sağlar.' },
    { q: 'Ücretsiz planda ne kadar AI Diyetisyen kullanabilirim?', a: 'Ücretsiz planda haftada 1 beslenme planı, günde 1 kalori hesaplama ve günde 3 AI sohbet mesajı hakkınız var. Premium ile tüm özellikler sınırsız!' },
    { q: 'Premium üyeliğimi istediğim zaman iptal edebilir miyim?', a: 'Evet, Premium üyeliğinizi dilediğiniz zaman iptal edebilirsiniz. İptal ettiğinizde, mevcut dönem sonuna kadar Premium avantajlarınızdan yararlanmaya devam edersiniz.' },
    { q: 'Ödeme güvenli mi?', a: 'Evet, tüm ödemeler iyzico altyapısı ile SSL şifrelemesi altında güvenle gerçekleştirilir. Kredi kartı bilgileriniz bizde saklanmaz.' },
    { q: 'Yıllık plan ile ne kadar tasarruf ediyorum?', a: 'Yıllık Premium planı ile aylık plana göre %28 tasarruf edersiniz. Aylık ₺149 yerine ₺107,50\'ye denk gelir.' }
  ];

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 via-white to-purple-50" data-testid="premium-page">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1604467731651-3d8b9c702b86?w=1920&q=80" alt="Premium Studio" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="inline-block glass-effect px-4 py-2 rounded-full mb-6">
            <Crown className="inline-block h-5 w-5 mr-2" />
            <span className="font-semibold">Premium Üyelik</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 drop-shadow-lg">
            Sağlıklı Yaşamın Anahtarı
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-3xl mx-auto drop-shadow">
            Sınırsız dersler, AI Diyetisyen tam erişim, kişiselleştirilmiş beslenme planları ve daha fazlası!
          </p>
        </div>
      </section>

      {/* Premium Status Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
        {renderPremiumStatusBanner()}
      </div>

      {/* AI Dietitian Highlight */}
      <section className="py-16 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-800 px-4 py-1">
              <Apple className="h-4 w-4 mr-2 inline" />
              Yeni Özellik
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">AI Diyetisyen</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Yapay zeka destekli kişisel beslenme asistanınız. Türk mutfağına uygun, sağlıklı ve lezzetli öneriler.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aiDietitianFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow border-green-100">
                  <CardHeader>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-green-600" />
                    </div>
                    <CardTitle className="text-lg">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-8">
            <Button size="lg" className="bg-green-600 hover:bg-green-700" onClick={() => navigate('/ai-dietitian')}>
              <Apple className="mr-2 h-5 w-5" />
              AI Diyetisyen'i Keşfet
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Planları Karşılaştır</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => {
              const buttonConfig = getButtonConfig(plan);
              return (
                <Card 
                  key={plan.id}
                  className={`relative ${plan.popular ? 'border-purple-500 border-2 shadow-2xl transform scale-105' : ''}`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-gradient-to-r from-purple-600 to-purple-800 text-white px-4 py-1">
                        <Sparkles className="h-3 w-3 mr-1 inline" />
                        {plan.badge}
                      </Badge>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-8 pt-8">
                    <CardTitle className="text-2xl mb-2">{plan.name}</CardTitle>
                    <div className="mt-4">
                      <span className="text-5xl font-bold">{plan.priceDisplay || plan.price}</span>
                      <span className="text-gray-600 ml-1">{plan.period}</span>
                    </div>
                    {plan.priceNote && (
                      <p className="text-sm text-green-600 mt-2 font-medium">{plan.priceNote}</p>
                    )}
                  </CardHeader>

                  <CardContent>
                    <ul className="space-y-3 mb-8">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          {feature.included ? (
                            <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="h-5 w-5 text-gray-300 mr-3 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={`text-sm ${feature.highlight ? 'font-semibold text-purple-600' : 'text-gray-700'}`}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className={`w-full ${plan.popular && !premiumInfo?.active ? 'bg-purple-600 hover:bg-purple-700 text-lg py-6' : ''} ${premiumInfo?.active && plan.id !== 'free' ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''}`}
                      variant={buttonConfig.variant}
                      size={plan.popular ? 'lg' : 'default'}
                      onClick={() => !buttonConfig.disabled && handleSubscribe(plan.id, plan.name, plan.price)}
                      disabled={buttonConfig.disabled || loading}
                    >
                      {loading && plan.id !== 'free' ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Yükleniyor...</>
                      ) : premiumInfo?.active && plan.id !== 'free' ? (
                        <><CheckCircle className="h-4 w-4 mr-2" /> {buttonConfig.text}</>
                      ) : (
                        buttonConfig.text
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Detaylı Karşılaştırma</h2>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-purple-50">
                    <tr>
                      <th className="text-left p-4 font-semibold">Özellik</th>
                      <th className="text-center p-4 font-semibold">Ücretsiz</th>
                      <th className="text-center p-4 font-semibold bg-purple-100">
                        <Crown className="inline h-5 w-5 mr-2 text-purple-600" />
                        Premium
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFeatures.map((feature, index) => (
                      <tr key={index} className="border-t">
                        <td className="p-4">{feature.name}</td>
                        <td className="p-4 text-center">
                          {feature.free ? (typeof feature.free === 'string' ? <span className="text-orange-600 text-sm">{feature.free}</span> : <Check className="h-5 w-5 text-green-600 mx-auto" />) : <X className="h-5 w-5 text-gray-300 mx-auto" />}
                        </td>
                        <td className="p-4 text-center bg-purple-50">
                          {feature.premium ? (typeof feature.premium === 'string' ? <span className="font-semibold text-purple-600">{feature.premium}</span> : <Check className="h-5 w-5 text-green-600 mx-auto" />) : <X className="h-5 w-5 text-gray-300 mx-auto" />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust & Security */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Shield className="h-16 w-16 text-purple-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4">Güvenli Ödeme</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center"><Shield className="h-5 w-5 text-purple-600 mr-2" />256-bit SSL Şifreleme</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">Tüm ödemeler iyzico altyapısı ile güvenle gerçekleştirilir.</p>
                <img src="/iyzico-logo-pack/checkout_iyzico_ile_ode/TR/Tr_Colored_Horizontal/iyzico_ile_ode_colored_horizontal.svg" alt="iyzico" className="h-6 mt-4 opacity-60" />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center"><Check className="h-5 w-5 text-green-600 mr-2" />İstediğin Zaman İptal</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">Üyeliğinizi dilediğiniz zaman iptal edebilirsiniz. Taahhüt yok!</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-gradient-to-b from-white to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Sıkça Sorulan Sorular</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left text-lg font-semibold">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-gray-600">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA - Only show if not premium */}
      {!premiumInfo?.active && (
        <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 drop-shadow-lg">Sağlıklı Yaşama Başla</h2>
            <p className="text-xl text-white/90 mb-8 drop-shadow">
              AI Diyetisyen ve sınırsız dersler ile hedeflerine ulaş!
            </p>
            <Button 
              size="lg" 
              className="bg-white text-purple-600 hover:bg-purple-50 text-lg px-8 py-6 shadow-xl"
              onClick={() => handleSubscribe('yearly', 'Yıllık Premium', 1290)}
              disabled={loading}
            >
              {loading ? <><Loader2 className="mr-2 h-6 w-6 animate-spin" /> Yükleniyor...</> : <><Crown className="mr-2 h-6 w-6" /> Premium'a Başla</>}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
};

export default PremiumPage;
